/**
 * Boost registry. A boost is collected from a pickup, kept in one of the player's
 * MAX_BOOST_SLOTS inventory slots and activated at any moment.
 *
 * To add a new boost: add its id to BOOST_TYPES, describe it in BOOSTS, implement
 * its effect in the player simulation (see game/), and draw its icon in the client.
 * Never reorder BOOST_TYPES: the index is used as a bit in network messages.
 */

export const BOOST_TYPES = ['turbo', 'shield', 'doubleJump', 'slowmo'] as const;
export type BoostType = (typeof BOOST_TYPES)[number];

/** How many boosts a player can hold at once. Pickups are ignored while the inventory is full. */
export const MAX_BOOST_SLOTS = 2;

export interface BoostDef {
  type: BoostType;
  /** Display name (Russian UI). */
  name: string;
  /** Short explanation for the UI. */
  description: string;
  /** Emoji for menus and toasts (the in-game icon is drawn by the client). */
  emoji: string;
  /** Main colour of the effect, CSS hex. */
  color: string;
  /** Effect length in game seconds (simulation time, so it also scales with timeScale). */
  durationSec: number;
  /** Multiplies how fast game time flows for this player (1.5 = 50% faster). */
  timeScale?: number;
  /** Spikes and crashes do not kill; falling into a pit bounces the player back up. */
  invulnerable?: boolean;
  /** Extra mid-air jumps (refilled on landing) while active. */
  airJumps?: number;
}

export const BOOSTS: Readonly<Record<BoostType, BoostDef>> = {
  turbo: {
    type: 'turbo',
    name: 'Турбо',
    description: 'Всё летит в полтора раза быстрее — догоняй соперников!',
    emoji: '⚡',
    color: '#ffd400',
    durationSec: 5,
    timeScale: 1.5,
  },
  shield: {
    type: 'shield',
    name: 'Щит',
    description: 'Неуязвимость: шипы и стены не страшны',
    emoji: '🛡️',
    color: '#38e1ff',
    durationSec: 4,
    invulnerable: true,
  },
  doubleJump: {
    type: 'doubleJump',
    name: 'Двойной прыжок',
    description: 'Можно подпрыгнуть ещё раз прямо в воздухе',
    emoji: '⏫',
    color: '#7dff5a',
    durationSec: 8,
    airJumps: 1,
  },
  slowmo: {
    type: 'slowmo',
    name: 'Замедление',
    description: 'Время замедляется — легче пройти сложное место',
    emoji: '🐢',
    color: '#b18cff',
    durationSec: 3,
    timeScale: 0.6,
  },
};

/** Bit used for a boost in the `fx` field of PlayerNetState. */
export function boostBit(type: BoostType): number {
  return 1 << BOOST_TYPES.indexOf(type);
}

/** Decodes an `fx` bitmask into the list of active boosts. */
export function boostsFromBits(bits: number): BoostType[] {
  return BOOST_TYPES.filter((_, i) => (bits & (1 << i)) !== 0);
}

export function isBoostType(value: unknown): value is BoostType {
  return typeof value === 'string' && (BOOST_TYPES as readonly string[]).includes(value);
}
